import { createFileRoute, useNavigate } from '@tanstack/react-router'
import * as Sentry from '@sentry/react'
import { assetPath } from '@/utils/assetPath'
import { useState, useCallback, useEffect } from 'react'
import { ArrowLeft } from 'lucide-react'
import { isIOSApp } from '@/utils/platformDetection'
import { FadeInUp } from '@/components/shared/atoms/FadeInUp'
import {
    PricingCard,
    PricingPlan,
} from '@/components/subscriptions/PricingCard'
import { GlossyButton } from '@/components/shared/atoms/GlossyButton'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import platformSubscriptionsApi from '@wtf/shared/api/platformSubscriptions'
import { DowngradeInSettingsBottomSheet } from '@/components/subscriptions/bottom-sheets/DowngradeInSettingsBottomSheet'
import { UpgradeSubscriptionBottomSheet } from '@/components/subscriptions/bottom-sheets/UpgradeSubscriptionBottomSheet'
import { usePricing } from '@/hooks/api/usePricing'
import { useToast } from '@/components/shared/toast/Toast'
import { useProtectedAction } from '@/hooks/useProtectedAction'
import { useCurrentUser } from '@/hooks/useCurrentUser'

export const Route = createFileRoute('/(onboarding)/(pricing)/pricing')({
    validateSearch: (search: Record<string, unknown>) => ({
        returnToPath:
            typeof search.returnToPath === 'string'
                ? search.returnToPath
                : undefined,
        reason: typeof search.reason === 'string' ? search.reason : undefined,
        preselect:
            typeof search.preselect === 'string' ? search.preselect : undefined,
    }),
    component: PricingRoute,
})

function PricingRoute() {
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { addToast } = useToast()
    const { withAuth } = useProtectedAction()
    const { isAuthenticated, loading: authLoading } = useCurrentUser()
    const { returnToPath, reason, preselect } = Route.useSearch()

    // TEMPORARY: Phase I iOS restriction - Remove in Phase II
    // Redirect iOS users away from payment pages to comply with App Store guidelines
    // See: docs/PHASE_I_TEMPORARY_IOS_RESTRICTIONS.md
    useEffect(() => {
        if (isIOSApp()) {
            navigate({ to: '/' })
        }
    }, [navigate])

    // TEMPORARY: Phase I iOS restriction - Remove in Phase II
    // Don't render pricing page on iOS while redirecting
    if (isIOSApp()) {
        return null
    }

    // Fetch user's current subscription
    const {
        data: subscription,
        isLoading: isLoadingSubscription,
        error: subscriptionError,
    } = useQuery({
        queryKey: ['platform-subscription'],
        queryFn: () => platformSubscriptionsApi.getCurrentSubscription(),
        enabled: isAuthenticated, // Only fetch when authenticated
        staleTime: 5 * 60 * 1000,
        retry: 3,
    })

    // Fetch pricing from Stripe
    const { data: pricing, isLoading: isPricingLoading } = usePricing()

    // Only wait for subscription if user is authenticated
    const isWaitingForSubscription = isAuthenticated && isLoadingSubscription
    const isPageLoading = authLoading || isPricingLoading || isWaitingForSubscription

    const currentPlan: PricingPlan =
        (subscription?.plan.toLowerCase() as PricingPlan) || 'free'

    // Auto-select next tier if they're out of credits or preselect is provided
    const getInitialSelection = (): PricingPlan | null => {
        if (preselect) {
            return preselect as PricingPlan
        }
        if (reason === 'out_of_credits') {
            // Suggest upgrade to next tier
            if (currentPlan === 'free') return 'pro'
            if (currentPlan === 'pro') return 'max'
            return 'max' // Already on max
        }
        return null // No plan selected by default
    }

    const [selectedPlan, setSelectedPlan] = useState<PricingPlan | null>(
        getInitialSelection()
    )
    const [showDowngradeModal, setShowDowngradeModal] = useState(false)
    const [showUpgradeModal, setShowUpgradeModal] = useState(false)
    const [pendingUpgradePlan, setPendingUpgradePlan] =
        useState<PricingPlan | null>(null)
    const [isProcessingUpgrade, setIsProcessingUpgrade] = useState(false)

    const handleSelectPlan = useCallback((plan: PricingPlan) => {
        setSelectedPlan(plan)
    }, [])

    const handleBack = useCallback(() => {
        if (returnToPath) {
            navigate({ to: returnToPath })
        } else {
            navigate({ to: '/' })
        }
    }, [navigate, returnToPath])

    const handleGoToSettings = useCallback(() => {
        navigate({ to: '/settings', search: { tab: 'Account' } })
    }, [navigate])

    const handleContinue = useCallback(async () => {
        if (!selectedPlan) return

        // If not authenticated, prompt login with return URL
        if (!isAuthenticated) {
            const returnUrl = `/pricing?preselect=${selectedPlan}${returnToPath ? `&returnToPath=${encodeURIComponent(returnToPath)}` : ''}`
            withAuth(() => {
                // After auth, user will be redirected back here
                // The plan will be pre-selected via the preselect param
            }, returnUrl)()
            return
        }

        // Check if user is downgrading
        const planHierarchy: Record<PricingPlan, number> = {
            free: 0,
            pro: 1,
            max: 2,
        }
        const currentLevel = planHierarchy[currentPlan]
        const selectedLevel = planHierarchy[selectedPlan]
        const isDowngrade = selectedLevel < currentLevel
        const isUpgrade = selectedLevel > currentLevel

        if (isDowngrade) {
            // Show modal directing to settings
            setShowDowngradeModal(true)
            return
        }

        if (selectedPlan === 'free' || selectedPlan === currentPlan) {
            // Free plan or same plan - return to wherever they came from
            if (returnToPath) {
                navigate({ to: returnToPath })
            } else {
                navigate({ to: '/' })
            }
            return
        }

        // UPGRADE LOGIC - Requires authentication
        if (isUpgrade) {
            withAuth(() => {
                if (currentPlan === 'free') {
                    // FREE → PRO/MAX: Use checkout (need payment method)
                    navigate({
                        to: '/checkout',
                        search: { plan: selectedPlan, returnToPath },
                    })
                } else {
                    // PRO → MAX: Show confirmation modal first
                    setPendingUpgradePlan(selectedPlan)
                    setShowUpgradeModal(true)
                }
            }, returnToPath || '/pricing')()
        }
    }, [selectedPlan, currentPlan, navigate, returnToPath, withAuth, isAuthenticated])

    const handleUpgradeConfirm = useCallback(async () => {
        if (!pendingUpgradePlan) return

        setIsProcessingUpgrade(true)
        try {
            const result = await platformSubscriptionsApi.changePlan(
                pendingUpgradePlan.toUpperCase() as 'PRO' | 'MAX'
                // No successUrl/cancelUrl needed!
            )

            if (result.immediate) {
                // Show success message
                addToast(
                    `Successfully upgraded to ${pendingUpgradePlan.toUpperCase()}!`,
                    'success'
                )
                // Invalidate queries to refresh subscription data
                queryClient.invalidateQueries({
                    queryKey: ['platform-subscription'],
                })
                // Navigate back
                if (returnToPath) {
                    navigate({ to: returnToPath })
                } else {
                    navigate({ to: '/' })
                }
            } else if (result.requiresCheckout && result.url) {
                // Fallback: open checkout in new tab if needed
                window.open(result.url, '_blank')
            }
        } catch (error) {
            Sentry.captureException(error, {
                tags: { action: 'upgrade_plan', component: 'PricingRoute' },
            })
            addToast(
                'Failed to upgrade. Please try again or contact support.',
                'error'
            )
        } finally {
            setIsProcessingUpgrade(false)
        }
    }, [pendingUpgradePlan, navigate, returnToPath, queryClient, addToast])

    if (subscriptionError && isAuthenticated) {
        // Only show error for authenticated users with actual errors
        return (
            <section className="relative flex h-full flex-col items-center justify-center px-4">
                <div className="text-center">
                    <p className="mb-4 text-white">
                        Unable to load subscription data. Please try again.
                    </p>
                    <GlossyButton onClick={() => navigate({ to: '/' })}>
                        Go Back
                    </GlossyButton>
                </div>
            </section>
        )
    }

    return (
        <section className="relative z-10 flex min-h-dvh flex-col pt-safe">
            <div className="flex px-2 pt-3 md:justify-center">
                <img
                    alt="logo-red"
                    src={assetPath('logo/logo-red.svg')}
                    className="h-[34px] w-[155px] md:mt-12 md:h-[100px] md:w-[440px]"
                />
            </div>

            <FadeInUp delay={0.3}>
                <div className="px-4 pt-4 pb-2 md:pt-16 md:pb-6 md:text-center">
                    <h1 className="font-sfpro text-28 leading-8 font-semibold text-white">
                        Pick a plan that works <br className="md:hidden" /> for
                        you
                    </h1>
                    {reason === 'out_of_credits' && (
                        <p className="mt-2 text-sm text-white/80">
                            You've used all your try-ons this month. Upgrade to
                            continue!
                        </p>
                    )}
                </div>
            </FadeInUp>

            <div className="mx-auto flex flex-1 flex-col px-2">
                <FadeInUp delay={0.5}>
                    <div className="mx-auto w-full max-w-6xl overflow-visible">
                        <div className="grid auto-rows-fr gap-3 px-3 pt-3 pb-2 md:grid-cols-3 md:gap-8 md:px-4 md:pt-4 md:pb-4">
                            <PricingCard
                                plan="free"
                                pricing={pricing?.free}
                                isSelected={selectedPlan === 'free'}
                                onSelect={handleSelectPlan}
                                isCurrentPlan={
                                    isAuthenticated && !isLoadingSubscription && currentPlan === 'free'
                                }
                            />
                            <PricingCard
                                plan="pro"
                                pricing={pricing?.pro}
                                isSelected={selectedPlan === 'pro'}
                                onSelect={handleSelectPlan}
                                isCurrentPlan={
                                    isAuthenticated && !isLoadingSubscription && currentPlan === 'pro'
                                }
                                showPopularBadge
                            />
                            <PricingCard
                                plan="max"
                                pricing={pricing?.max}
                                isSelected={selectedPlan === 'max'}
                                onSelect={handleSelectPlan}
                                isCurrentPlan={
                                    isAuthenticated && !isLoadingSubscription && currentPlan === 'max'
                                }
                            />
                        </div>
                    </div>
                </FadeInUp>

                <FadeInUp delay={0.7} className="mt-auto mb-6">
                    <div className="flex items-center gap-2 pt-6">
                        <GlossyButton
                            variant="ghost"
                            className="rounded-xl bg-white/20 px-4 py-[14px] text-sm font-medium text-white backdrop-blur-md"
                            onClick={handleBack}
                        >
                            <ArrowLeft
                                strokeWidth={1}
                                className="mr-1 h-5 w-5"
                            />
                            Back
                        </GlossyButton>
                        <GlossyButton
                            className="w-full rounded-xl py-[14px] text-sm"
                            onClick={handleContinue}
                            disabled={
                                !selectedPlan ||
                                isPageLoading ||
                                isProcessingUpgrade
                            }
                        >
                            {isPageLoading
                                ? 'Loading...'
                                : isProcessingUpgrade
                                    ? 'Processing upgrade...'
                                    : 'Continue'}
                        </GlossyButton>
                    </div>
                </FadeInUp>
            </div>

            <DowngradeInSettingsBottomSheet
                open={showDowngradeModal}
                onClose={() => setShowDowngradeModal(false)}
                onGoToSettings={handleGoToSettings}
            />

            <UpgradeSubscriptionBottomSheet
                open={showUpgradeModal}
                onClose={() => {
                    setShowUpgradeModal(false)
                    setPendingUpgradePlan(null)
                }}
                currentPlan={currentPlan.toUpperCase() as 'PRO'}
                targetPlan={
                    (pendingUpgradePlan?.toUpperCase() || 'MAX') as 'MAX'
                }
                onConfirm={handleUpgradeConfirm}
                nextRenewalDate={subscription?.currentPeriodEnd}
            />
        </section>
    )
}
